import { useContext } from 'react'
import { useTranslation } from 'react-i18next'
import { common, controls } from '@ocean/ui'
import { DateUtils } from '@ocean/utils'
import { EnvironmentUtils, UserUtils } from 'utils'
import { AccountTypes } from 'modules/Auth/types'
import {
  SessionListItem,
  SessionListItemHeader,
  SessionListItemActions,
  SessionListItemInfoWrapper,
  SessionListItemInfo,
  SessionListItemInfoLabel,
  SessionListItemSidebar,
  ButtonWrapper,
} from 'modules/simulatorTrainings/common/components/common/ScheduleSession.styled'
import { EnrolledPersonContext } from 'modules/simulatorTrainings/common/contexts'
import { namespace } from 'modules/simulatorTrainings/common/localization'
import {
  SimulatorTrainingScheduleSession,
  SimulatorTrainingSessionType,
} from 'modules/simulatorTrainings/common/types'

const { Typography } = common
const { Button } = controls

const I18_PATH = `${namespace}:simulatorTraining.scheduleVirtualClassroomSession`

type ScheduleVirtualClassroomSessionProps = {
  session: SimulatorTrainingScheduleSession
  sessionTypes: Record<SimulatorTrainingSessionType, string>
  onStart: (sessionId: Guid) => void
}

const ScheduleVirtualClassroomSession: React.FC<ScheduleVirtualClassroomSessionProps> = props => {
  const { t } = useTranslation()
  const { session, onStart, sessionTypes } = props
  const isUserEnrolledPerson = useContext(EnrolledPersonContext)
  const { sessionId, sessionName, type, startDate, endDate } = session
  const localStartDate = DateUtils.convertUTCDateToLocalDate(startDate)

  const { type: userType } = UserUtils.getUserAccountInfo()
  const isSpecialUser = userType === AccountTypes.SpecialUser
  const { instructorLedStartSessionAvailable } =
    EnvironmentUtils.getPermissions('simulatorTraining')
  const isNotEndedSession =
    DateUtils.convertUTCDateToLocalDate(endDate).getTime() > new Date().getTime()

  const showActions =
    isUserEnrolledPerson &&
    isNotEndedSession &&
    (!isSpecialUser || instructorLedStartSessionAvailable)

  function handleStartClick() {
    onStart(sessionId)
  }

  function renderActions() {
    return (
      <ButtonWrapper>
        <Button
          title={t(`${I18_PATH}.join`)}
          onClick={handleStartClick}
          dataTest='ScheduleVirtualClassroomSessionClassroomStart'
          fill
        />
      </ButtonWrapper>
    )
  }

  return (
    <SessionListItem key={sessionId}>
      <SessionListItemHeader>
        <Typography variant='small' data-test='ScheduleVirtualClassroomSessionType'>
          {sessionTypes[type]}
        </Typography>
        <Typography variant='h3' data-test='ScheduleVirtualClassroomSessionName'>
          {sessionName}
        </Typography>
      </SessionListItemHeader>
      <SessionListItemActions>
        <SessionListItemInfoWrapper>
          <SessionListItemInfo>
            <SessionListItemInfoLabel>
              <Typography
                variant='small'
                uppercase
                data-test='ScheduleVirtualClassroomSessionStartTimeLabel'
              >
                {t(`${I18_PATH}.startTime`)}
              </Typography>
            </SessionListItemInfoLabel>
            <Typography variant='small' data-test='ScheduleVirtualClassroomSessionStartTime'>
              {DateUtils.formatShortTime(localStartDate)}
            </Typography>
          </SessionListItemInfo>
          <SessionListItemInfo>
            <SessionListItemInfoLabel>
              <Typography variant='small' uppercase data-test='ScheduleVirtualClassroomSessionType'>
                {t(`${I18_PATH}.duration`)}
              </Typography>
            </SessionListItemInfoLabel>
            <Typography variant='small' data-test='ScheduleVirtualClassroomSessionDuration'>
              {DateUtils.getDurationBetweenDates(startDate, endDate)}
            </Typography>
          </SessionListItemInfo>
        </SessionListItemInfoWrapper>
        {showActions && <SessionListItemSidebar>{renderActions()}</SessionListItemSidebar>}
      </SessionListItemActions>
    </SessionListItem>
  )
}

export default ScheduleVirtualClassroomSession
